import { useEffect, useMemo, useState } from 'react';

import { AUTH_API_URL, FILE_MGMT_PERMISSION } from 'lib/constants';
import { getCookie } from 'lib/cookies';
import { processPermissions, processPermissionsV2, sortWorkspaces } from 'lib/helpers';
import { Workspace, WorkspacePermissionV1, WorkspacePermissionV2 } from 'lib/types';

import { fetchData } from './fetchData';

const useWorkspaceDetails = () => {
    const [allWorkspaces, setAllWorkspaces] = useState<Workspace[] | null>(null);
    const [allPermissions, setAllPermissions] = useState<WorkspacePermissionV1[]>([]);
    const [allPermissionsV2, setAllPermissionsV2] = useState<WorkspacePermissionV2[]>([]);
    const [currentWorkspacePermissions, setCurrentWorkspacePermissions] = useState<WorkspacePermissionV2>();

    useEffect(() => {
        const fetchUserDetails = async () => {
            const { error, data } = await fetchData({
                url: `${AUTH_API_URL}/detail`,
                method: 'GET',
                body: {},
            });
            const currentWorkspaceId = getCookie('helio-workspace')?.id;

            if (!error) {
                const fetchedWorkspaces = data[0].workspace_users
                    ?.filter(({ workspace }: { workspace: Workspace }) => workspace)
                    .map(({ workspace }: { workspace: Workspace }) => workspace)
                    .sort(sortWorkspaces);
                const userPermissions = processPermissions(data[0].workspace_users);
                const userPermissionsV2 = processPermissionsV2(data[0].workspace_users);

                setAllWorkspaces(fetchedWorkspaces);
                setAllPermissions(userPermissions);
                setAllPermissionsV2(userPermissionsV2);
                setCurrentWorkspacePermissions(
                    userPermissionsV2.find(
                        (workspacePermission) => workspacePermission.workspaceId === currentWorkspaceId,
                    ),
                );
            }
        };

        fetchUserDetails();
    }, []);

    const currentUserHasFileManagerPermission = useMemo(
        () => !!currentWorkspacePermissions?.permissions.find((permission) => permission.name === FILE_MGMT_PERMISSION),
        [currentWorkspacePermissions],
    );

    return {
        allWorkspaces,
        allPermissions,
        allPermissionsV2,
        currentWorkspacePermissions,
        currentUserHasFileManagerPermission,
    };
};

export default useWorkspaceDetails;
